<template>
  <div id="app">
    <header>
		<div class="header-left">
			<img src="/assets/img/logo.webp" alt="JY">
		</div>
		<nav class="header-right">
			<a href="#about">About</a>
			<a href="#experience">Experience</a>
			<a href="#portfolio">Portfolio</a>
			<a href="#contact">Contact</a>
		</nav>
    </header>
    <main>
		<section class="hero">
			<div class="hero-content">
				<img src="/assets/img/logo.webp" alt="JY">
				<h1 class="hero-title">Web/Mobile <br> Designer & Developer</h1>
				<p class="hero-subtitle">Making ideas happen.</p>
				<div class="scroll-down-indicator" @click="scrollToContent" style="">
					<!-- <span>Scroll</span> -->
					<i class="fa fa-arrow-down"></i>
				</div>
			</div>
		</section>
		<section id="about" class="about">
			<div class="about-content">
				<img src="/assets/img/profile.webp" alt="Josh York">
				<h2>Hi, I'm <strong>Josh York</strong></h2>
				<p class="subheading">Based in New Zealand, I bring over 15 years of freelance and startup experience to every project.</p>
				<p class="subheading">My approach blends clean, responsive design with scalable, high-performance development, ensuring each product is both visually stunning and technically sound. From concept to launch, I focus on making complex ideas simple, intuitive, and impactful.</p>
			</div>
		</section>
		<section id="experience" class="experience">
			<div class="experience-content">
				<h2>My Experience</h2>
				<p class="subheading">From early web builds to modern, full-stack applications, my experience spans multiple technologies and approaches, shaped by years of hands-on development.</p>
				<div class="experience-content-wrapper">
					<div class="experience-content-item">
						<i class="fa fa-code"></i>
						<h3>Frontend Development</h3>
						<p>Building fast, accessible, and responsive user interfaces that deliver exceptional user experiences. Using modern technologies like HTML5, CSS3, JavaScript, and frameworks such as Vue.js. My focus is on translating design concepts into seamless, interactive, and high-performing digital products.</p>
						<ul class="experience-content-item-list">
							<li>HTML</li>
							<li>CSS</li>
							<li>Vue.js</li>
							<li>JavaScript</li>
							<li>jQuery</li>
							<li>Less</li>
							<li>Sass</li>
							<li>Wordpress</li>
						</ul>
					</div>
					<div class="experience-content-item">
						<i class="fa fa-terminal"></i>
						<h3>Backend & APIs</h3>
						<p>Reliable backend architecture is the foundation of any great digital product. From designing robust databases to developing RESTful APIs, each system is built for security, scalability, and efficiency. I create infrastructures that keep data flowing and features running smoothly.</p>
						<ul class="experience-content-item-list">
							<li>C#</li>
							<li>.NET Core</li>
							<li>AI</li>
							<li>PHP</li>
							<li>MySQL</li>
							<li>Java</li>
							<li>Python</li>
							<li>AWS</li>
						</ul>
					</div>
					<div class="experience-content-item">
						<i class="fa fa-mobile"></i>
						<h3>Mobile App Development</h3>
						<p>High-performance mobile applications bring ideas to users’ fingertips. Whether for iOS or Android, or cross-platform deployment that feel native on every device. Each project blends sleek design with powerful backend integration to create engaging, scalable mobile experiences.</p>
						<ul class="experience-content-item-list">
							<li>iOS</li>
							<li>WatchOS</li>
							<li>Android</li>
						</ul>
					</div>
				</div>
			</div>
		</section>
		<section id="portfolio" class="portfolio">
			<div class="portfolio-content">
				<h2>Featured Work</h2>
				<p class="subheading">My journey in web and mobile development has taken me from small passion projects to large-scale applications. Here’s a closer look at some of my work.</p>
				<div class="portfolio-content-wrapper">
					<div v-for="item in model.featured" class="portfolio-content-item">
						<span class="background" :style="`background-image:url('/assets/img/portfolio/boxes/${item.image}.webp');`"></span>
						<div class="info">
							<h4 :title="item.name">{{ item.name }}</h4>
							<p>{{ item.description }}</p>
							<ul class="portfolio-content-item-list">
								<li v-for="tech in item.tech">{{ tech }}</li>
							</ul>
							<a v-if="item.url" :href="item.url" target="_blank">View Project</a>
						</div>
					</div>
				</div>
				<div class="portfolio-content-wrapper -small" v-bind:class="{ 'more' : model.showMore }">
					<div class="small-wrapper">
						<div v-for="item in model.portfolio" class="portfolio-content-item">
							<span class="background" :style="`background-image: url('/assets/img/portfolio/boxes/${item.image}.webp');`"></span>
							<div class="info">
								<h4 :title="item.name">{{ item.name }}</h4>
								<p>{{ item.description }}</p>
								<ul class="portfolio-content-item-list">
									<li v-for="tech in item.tech">{{ tech }}</li>
								</ul>
							</div>
						</div>
					</div>
				</div>
				<button v-if="!model.showMore" v-on:click="model.showMore = true" class="more">Show More <i class="fa fa-arrow-down"></i></button>
				<button v-else v-on:click="model.showMore = false" class="less">Show Less <i class="fa fa-arrow-up"></i></button>
			</div>
		</section>
		<section id="contact" class="contact">
			<div class="contact-content">
				<h2>Let's Work Together</h2>
				<div class="contact-content-wrapper">
					<div class="contact-info">
						<h3>Get in Touch</h3>
						<p>I'm always interested in new opportunities and exciting projects. Whether you have a question or just want to say hi, feel free to reach out!</p>
						<a href="mailto:info@joshyork.com"><i class="fa fa-envelope-o"></i> info@joshyork.com</a>
						<a href="https://www.linkedin.com/in/joshyork" target="_blank"><i class="fa fa-linkedin"></i> LinkedIn</a>
					</div>
					<div>
						<div class="contact-form">
							<h4>Send a Message</h4>
							<div class="form-group">
								<input v-on:keyup="clearError" type="text" name="firstName" placeholder="First Name" v-model="model.form.firstName" />
								<input v-on:keyup="clearError" type="text" name="lastName" placeholder="Last Name" v-model="model.form.lastName" />
								<input type="text" class="hidden" name="hidden" placeholder="Last Name" v-model="model.hiddenInput" />
							</div>
							<div>
								<input v-on:keyup="clearError" type="email" name="email" placeholder="Email" v-model="model.form.email" />
							</div>
							<div>
								<textarea v-on:keyup="clearError" id="message" name="message" placeholder="Your message" v-model="model.form.message"></textarea>
							</div>
							<p v-if="model.formSuccess" class="success">{{ model.formSuccess }}</p>
							<p v-if="model.formError" class="error">{{ model.formError }}</p>
							<button @click="send" type="button">Send Message <i class="fa fa-paper-plane-o"></i></button>
						</div>
					</div>
				</div>
			</div>
		</section>
    </main>
  </div>
</template>

<script>
export default {
	name: 'App',
	data() {
		return {
			model: {
				featured: [{
					name: 'CodeAvengers.com',
					description: 'Learn how to code website, apps and games through fun and effective online courses.',
					tech: ['HTML', 'CSS', 'Vue.js', 'Javascript', 'JQuery', 'Less', 'Recurly', 'Stripe'],
					url: 'https://www.codeavengers.com',
					image: '4',
				}, {
					name: 'Excursion.nz',
					description: 'An interactive NZ travel map to discover top attractions and hidden gems along your route.',
					tech: ['HTML', 'CSS', 'Vue.js', 'Javascript', 'PHP', 'Google Maps', 'Stripe', 'SEO', 'Blog'],
					url: 'https://excursion.nz',
					image: '29',
				}, {
					name: 'Colorize.SpeedImg.com',
					description: 'Instantly Colorize Black & White images.',
					tech: ['AI', 'HTML', 'CSS', 'Vue.js', 'Stripe'],
					url: 'https://colorize.speedyimg.com',
					image: '34',
				}, {
					name: 'Speedyimg.com',
					description: 'Instantly compress and convert PNG, JPG, and WebP images with SpeedyIMG.',
					tech: ['HTML', 'CSS', 'Vue.js', 'Javascript', 'JQuery', 'Less', 'Stripe'],
					url: 'https://speedyimg.com',
					image: '30',
				}, {
					name: 'LookSharpProduce.com',
					description: 'A new modern website for a Pacific Island food supplier in New Zealand.',
					tech: ['HTML', 'CSS', 'Vue.js', 'Javascript', 'PHP', 'SEO', 'Blog'],
					url: 'https://looksharpproduce.co.nz',
					image: '33',
				}, {
					name: 'Sgtwoofy.com',
					description: 'An interactive card battling platform that can be integrated with Twitch streams.',
					tech: ['HTML', 'CSS', 'Vue.js', 'Javascript', 'JQuery', 'Less', 'PHP', 'Twitch API'],
					url: 'https://itunes.apple.com/app/minewars/id516336144?mt=8',
					image: '32',
					url: 'https://sgtwoofy.com',
				}, {
					name: 'Alpha Remote',
					description: 'A Apple Watch remote for controling you Sony Alpha camera right from the watch.',
					tech: ['iOS', 'watchOS', 'Objective-C', 'Sony Alpha API'],
					image: '31',
				}, {
					name: 'MineWars',
					description: 'An exciting game where you try to buy precious metals low and sell them high.',
					tech: ['iOS', 'Web Services', 'PHP', 'JSON'],
					image: '6',
				}],
				portfolio: [{
					name: 'ListenPort',
					description: 'Engage with your customer via SMS to collect feedback and suggestions and reply instantly to solve problems.',
					tech: ['HTML', 'CSS', 'PHP', 'Javascript', 'JQuery', 'SMS'],
					image: '5',
				}, {
					name: 'LDS Gamebox',
					description: 'A themed gamebox with a variety of game such as Dots, Tic Tac Toe and Hangman.',
					tech: ['iOS'],
					image: '2',
				}, {
					name: 'Big Notes',
					description: 'Big Notes helps the visually impaired take notes and read them with ease.',
					tech: ['Android', 'HTML', 'CSS', 'PhoneGap', 'JQuery', 'Responsive'],
					image: '8',
				}, {
					name: 'Countess Luann',
					description: 'Countess Luann\'s mobile app contains unique features such as "Etiquette & Manners Tips, Soundbites, Etiquette & Manners Polls and Quizzes.',
					tech: ['iOS', 'PHP', 'JSON', 'Web Services'],
					image: '28',
				}, {
					name: 'Jay Heiron',
					description: 'Jay Hieron\'s iOS app contains a unique feature called the "Sports Bar Locator" so users can find places to go and watch MMA fights live.',
					tech: ['iOS', 'PHP', 'JSON', 'Web Services'],
					image: '27',
				}, {
					name: 'All American Steakhouse',
					description: 'Interactive features include the menu, specials, directions to each location, polls, quizzes and "Ask The Steakhouse."',
					tech: ['iOS', 'PHP', 'JSON', 'Web Services'],
					image: '26',
				}, {
					name: 'Ford\'s Fish Shack',
					description: 'Interactive features include the menu, specials, directions to each location, polls, quizzes and "Ask The Fish Shack."',
					tech: ['iOS', 'PHP', 'JSON', 'Web Services'],
					image: '25',
				}, {
					name: 'Blast in a Glass',
					description: 'The official Deena Nicole iOS App. View his bio, photos, quizzes, polls and even ask questions.',
					tech: ['iOS', 'Web Services', 'PHP', 'JSON'],
					image: '24',
				}, {
					name: 'Sonoma Deals',
					description: 'Local Deals & Coupons App for the Sonoma County, CA area.',
					tech: ['iOS', 'PHP', 'JSON', 'Web Services'],
					image: '23',
				}, {
					name: 'The Matrix App',
					description: 'The official Shawn Marion iOS App. View his bio, photos, quizzes, polls and even ask questions.',
					tech: ['iOS', 'Web Services', 'PHP', 'JSON'],
					image: '22',
				}, {
					name: 'Deena\'s Merp Words',
					description: 'With Deena Nicole. This game takes word playing to the next level!',
					tech: ['iOS', 'Web Services'],
					image: '21',
				}, {
					name: 'Dulles Deals',
					description: 'Local Deals & Coupons App for the Dulles, VA and Loudoun County, VA area.',
					tech: ['iOS', 'PHP', 'JSON', 'Web Services'],
					image: '20',
				}, {
					name: 'The Matrix Word Dunk',
					description: 'With Shawn Marion. This game takes word playing to the next level!',
					tech: ['iOS', 'Web Services'],
					image: '19',
				}, {
					name: 'The Truth\'s All-Star Words',
					description: 'With Paul Pierce. This game takes word playing to the next level!',
					tech: ['iOS', 'Web Services'],
					image: '18',
				}, {
					name: 'Gubbsearthship.com',
					description: 'A quick wordpress site I was asked to put together.',
					tech: ['HTML', 'CSS', 'Wordpress'],
					image: '17',
				}, {
					name: 'Big Papi Word Slam',
					description: 'With David Ortiz. This game takes word playing to the next level!',
					tech: ['iOS', 'Web Services'],
					image: '16',
				}, {
					name: 'Paul Pierce',
					description: 'The official Paul Pierce The Truth iOS App. View his bio, photos, quizzes, polls and even ask questions.',
					tech: ['iOS', 'Web Services', 'PHP', 'JSON'],
					image: '15',
				}, {
					name: 'Palavras Cruzadas',
					description: 'Palavras Cruzadas is a "Scrabble" like game translated into Portugues',
					tech: ['iOS', 'Web Services'],
					image: '14',
				}, {
					name: 'Tedyorkphotography.com',
					description: 'Ted York\'s portfolio for his professional photography business.',
					tech: ['HTML', 'CSS', 'SEO', 'PHP', 'Flash'],
					image: '13',
				}, {
					name: 'Flowersbylegacy',
					description: 'Blooms Today\'s mobile version of their sympathy flowers website.',
					tech: ['HTML', 'CSS', 'JQuery Mobile', 'AJAX', 'PHP', 'QAS'],
					image: '12',
				}, {
					name: 'Bloomstoday',
					description: 'Blooms Today\'s mobile version for the main site.',
					tech: ['HTML', 'CSS', 'JQuery Mobile', 'AJAX', 'PHP', 'QAS'],
					image: '11',
				}, {
					name: 'Healthwatchutah.com',
					description: 'Rebuilt an existing website to be fully responsive.',
					tech: ['HTML', 'CSS', 'Responsive', 'Bootstrap'],
					image: '10',
				}, {
					name: 'My Faith In God',
					description: 'App to track progress in the My Faith In God program.',
					tech: ['iOS', 'Android', 'Web Services', 'PHP', 'JSON'],
					image: '7',
				}, {
					name: 'David Ortiz',
					description: 'The official David Oriz Big Papi iOS App. View his bio, photos, quizzes, polls and even ask questions.',
					tech: ['iOS', 'Web Services', 'PHP', 'JSON'],
					image: '3',
				}, {
					name: 'Lucasmarc.com',
					description: 'Rebuilt an existing website to be fully responsive.',
					tech: ['HTML', 'CSS', 'Responsive', 'Bootstrap'],
					image: '1',
				}],
				form: {
					firstName: '',
					lastName: '',
					email: '',
					message: '',
				},
				formSuccess: '',
				formError: '',
				hiddenInput: '',
				showMore: false,
			}
		}
	},
	computed: {
		isValidEmail() {
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			return emailRegex.test(this.model.form.email);
		}
	},
	methods: {
		clearError() {
			const c = this;

			if (
				c.model.form.firstName.trim() &&
				c.model.form.lastName.trim() &&
				c.model.form.email.trim() &&
				c.model.form.message.trim() &&
				c.isValidEmail
			) {
				c.model.formError = '';
			}

		},
		async send() {
			const c = this;

			c.model.formError = '';

			if (c.model.hiddenInput !== '') {
				return;
			}

			if (
				!c.model.form.firstName.trim() ||
				!c.model.form.lastName.trim() ||
				!c.model.form.email.trim() ||
				!c.model.form.message.trim()
			) {
				c.model.formError = 'Please fill in all fields.';
				return;
			}

			if (!c.isValidEmail) {
				c.model.formError = 'Please enter a valid email address.';
				return;
			}

			const url = `${window.location.protocol}//${window.location.host}/mail.php`;

			try {
				const response = await fetch(url, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json'
					},
					body: JSON.stringify(c.model.form)
				});

				if (!response.ok) {
					c.model.formError = 'There was an error sending your message. Please try again later.';
				}

				c.model.formSuccess = 'Thanks for the message! I will get back to you ASAP.';

				// Optionally reset form
				c.model.form.firstName = '';
				c.model.form.lastName = '';
				c.model.form.email = '';
				c.model.form.message = '';
				c.model.formError = '';

				setTimeout(function() {
					c.model.formSuccess = '';
				}, 2000);

			} catch (error) {
				c.model.formError = 'There was an error sending your message. Please try again later.';
			}
		}
	}
}
</script>

<style lang="less">
// @import "../src/styles/main.less";
</style> 